<template>
  <div v-if="showModal" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">{{ isEditMode ? $t('editCategory') : $t('newCategory') }}</h2>
        <button class="close-btn" @click="closeModal">
          <svg-icon name="ic_close" original class="w-6 h-6" />
        </button>
      </div>

      <form class="modal-body" @submit.prevent="submitForm">
        <div class="form-group">
          <label for="label" class="form-label">{{ $t('categoryLabel') }}</label>
          <input
            id="label"
            v-model="formData.label"
            type="text"
            class="form-input"
            :placeholder="$t('categoryLabel')"
            required
          />
        </div>

        <div class="form-group">
          <label for="type" class="form-label">{{ $t('type') }}</label>
          <select id="type" v-model="formData.type" class="form-input" required>
            <option value="">{{ $t('selectType') }}</option>
            <option value="QUIZ">Quiz</option>
          </select>
        </div>

        <div class="form-group">
          <label for="description" class="form-label">{{ $t('description') }}</label>
          <textarea
            id="description"
            v-model="formData.description"
            class="form-input"
            rows="3"
            :placeholder="$t('description')"
            required
          />
        </div>

        <div class="form-group">
          <label for="icon" class="form-label">{{ $t('categoryIcon') }}</label>
          <input
            id="icon"
            v-model="formData.icon"
            type="text"
            class="form-input"
            placeholder="ic_default"
          />
        </div>

        <div v-if="errorMessage" class="form-error text-red-600 mb-4">{{ errorMessage }}</div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="closeModal">
            {{ $t('cancelButton') }}
          </button>
          <button type="submit" class="btn-submit" :disabled="isLoading">
            <loading
              v-if="isLoading"
              :active="isLoading"
              :can-cancel="false"
              :is-full-page="false"
              background-color="transparent"
              color="#FFFFFF"
              loader="dots"
              :height="20"
            />
            <span v-else>{{ isEditMode ? $t('updateButton') : $t('addButton') }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import http from '@/plugins/http'
import apiRoute from '@/router/api-routes'

export default {
  name: 'create-categorie',
  components: { Loading },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      showModal: false,
      isLoading: false,
      isEditMode: false,
      errorMessage: '',
      formData: {
        label: '',
        type: '',
        description: '',
        icon: '',
        id: null
      }
    }
  },
  watch: {
    show (newVal) {
      this.showModal = newVal
      this.errorMessage = ''
      if (newVal && this.editData && this.editData.id) {
        this.isEditMode = true
        this.loadEditData()
      } else {
        this.isEditMode = false
        this.resetForm()
      }
    }
  },
  methods: {
    loadEditData () {
      if (this.editData) {
        this.formData.id = this.editData.id
        this.formData.label = this.editData.label || ''
        this.formData.type = this.editData.type || ''
        this.formData.description = this.editData.description || ''
        this.formData.icon = this.editData.icon || ''
      }
    },
    async submitForm () {
      this.errorMessage = ''

      if (!this.formData.label || !this.formData.type || !this.formData.description) {
        this.errorMessage = this.$t('errorAddAdmin')
        return
      }

      this.isLoading = true

      try {
        const payload = {
          label: this.formData.label,
          type: this.formData.type,
          description: this.formData.description,
          icon: this.formData.icon || 'ic_default'
        }

        const headers = {
          Authorization: `Bearer ${this.$store.getters.loginToken}`,
          'Content-Type': 'application/json'
        }

        if (this.isEditMode) {
          const payloadWithId = Object.assign({ id: this.formData.id }, payload)
          await http.patch(apiRoute.updateCategory, payloadWithId, { headers })
        } else {
          await http.post(apiRoute.createCategory, payload, { headers })
        }
        this.$emit('categoryCreated')

        this.resetForm()
        this.closeModal()
      } catch (error) {
        console.error(error)
        this.errorMessage = (error && error.data && error.data.message) || this.$t('errorAddAdmin')
      } finally {
        this.isLoading = false
      }
    },
    resetForm () {
      this.formData = { label: '', type: '', description: '', icon: '', id: null }
      this.isEditMode = false
      this.errorMessage = ''
    },
    closeModal () {
      this.showModal = false
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
@import "src/assets/style/sass/variables";

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: $black;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { opacity: 0.7; }
}

.modal-body {
  padding: 25px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: $black;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.3s;
  &:hover { border-color: #bbb; }
}

textarea.form-input {
  resize: vertical;
  min-height: 80px;
}

.modal-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.btn-cancel,
.btn-submit {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cancel {
  background-color: #e0e0e0;
  color: $black;
  &:hover { background-color: #d0d0d0; }
}

.btn-submit {
  background-color: #0e6ba8;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  &:disabled { opacity: 0.7; cursor: not-allowed; }
}
</style>
